// Web Speech API dictation engine — the LEGACY path (Chrome/Edge/Android only;
// does NOT exist in iOS WKWebView, which is why Deepgram is the target). Kept so
// REACT_APP_DICTATION_PROVIDER can default to current behavior until the Deepgram
// BAA is signed. Behavior mirrors the original inline SpeechRecognition in
// DictateNotes/VoiceNoteButton exactly.

import {
  DictationCallbacks,
  DictationEngine,
  DictationStartOptions,
} from './types';

function getSpeechRecognitionCtor(): (new () => any) | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export class WebSpeechDictationEngine implements DictationEngine {
  readonly provider = 'webspeech' as const;
  private rec: any = null;
  private cb: DictationCallbacks;
  private language = 'en-US';
  private running = false;

  constructor(callbacks: DictationCallbacks) {
    this.cb = callbacks;
  }

  isSupported(): boolean {
    return !!getSpeechRecognitionCtor();
  }

  async start(opts?: DictationStartOptions): Promise<void> {
    if (opts?.language) this.language = opts.language;
    this.running = true;
    this.startRecognition();
  }

  private startRecognition() {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      this.cb.onError?.('Voice input is not supported in this browser.');
      this.cb.onStatus?.('error');
      return;
    }
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = this.language;
    rec.onstart = () => this.cb.onStatus?.('live');
    rec.onresult = (ev: any) => {
      let interimText = '';
      let finalChunk = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const piece = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalChunk += piece;
        else interimText += piece;
      }
      if (finalChunk) this.cb.onFinal(finalChunk.trim());
      this.cb.onInterim(interimText);
    };
    rec.onerror = (ev: any) => {
      if (ev?.error && ev.error !== 'no-speech') {
        this.cb.onError?.(`Voice input error: ${ev.error}`);
      }
    };
    rec.onend = () => {
      this.cb.onInterim('');
      this.rec = null;
    };
    this.rec = rec;
    try {
      rec.start();
    } catch {
      /* noop */
    }
  }

  pause(): void {
    try {
      this.rec?.stop();
    } catch {
      /* noop */
    }
  }

  resume(): void {
    if (this.running && !this.rec) this.startRecognition();
  }

  async stop(): Promise<void> {
    this.running = false;
    try {
      this.rec?.stop();
    } catch {
      /* noop */
    }
    this.rec = null;
    this.cb.onStatus?.('stopped');
  }
}
